/**
 * AgentOsOnboardingWizard.jsx
 *
 * 5-step value-first onboarding flow:
 *   1. Vyber agenta              (no API)
 *   2. Tvoj brand                (no API)
 *   3. Naživo Preview            (calls /agent-os/demo/review-reply)
 *   4. Email + Aktivácia          (POST /agent-os/onboarding/activate)
 *   5. Success                    (FE only)
 *
 * Progress persisted to localStorage so refresh / partial-completion
 * doesn't lose the state. Backend is hit only on step 4 (activate)
 * + step 3 (one demo gen). This keeps the wizard snappy and the LLM
 * budget tight.
 */
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API } from "../config";
import {
  Bot, ArrowRight, ArrowLeft, CheckCircle, Loader2, Sparkles,
  Star, MessageCircle, ShoppingCart, Mail, FileText, ShieldCheck,
} from "lucide-react";
import { AgentOsPixelTracker } from "../components/agent-os/AgentOsShared";

const STORAGE_KEY = "trp_agent_os_wizard_v1";

const AGENTS = [
  { id: "review_reply",  name: "Review Reply Agent",      desc: "Odpovedá na Google + Heureka recenzie 24/7",          icon: <Star className="w-7 h-7" />,         price: 79,  ready: true  },
  { id: "cart_recovery", name: "Cart Recovery Agent",     desc: "AI-personalized email + SMS recovery flow",            icon: <ShoppingCart className="w-7 h-7" />,  price: 99,  ready: false },
  { id: "newsletter",    name: "Newsletter Marketing",    desc: "Plne automatický týždenný newsletter z katalógu",      icon: <Mail className="w-7 h-7" />,         price: 79,  ready: false },
  { id: "product_desc",  name: "Product Desc Optimizer",  desc: "SEO + GEO rewrite v 26 jazykoch",                       icon: <FileText className="w-7 h-7" />,      price: 79,  ready: false },
  { id: "gdpr_watch",    name: "GDPR + A11y Watch",       desc: "Denný compliance scan + auto-fix",                      icon: <ShieldCheck className="w-7 h-7" />,   price: 49,  ready: false },
];

const VOICES = [
  { id: "warm",     name: "Vrúcny",     desc: "Empatický, ľudský" },
  { id: "formal",   name: "Formálny",   desc: "Korporátny, profi" },
  { id: "friendly", name: "Priateľský", desc: "Casual, tykanie" },
  { id: "premium",  name: "Premium",    desc: "Luxusný, low-key" },
];

const LANGS = [
  { id: "sk", name: "Slovenčina" },
  { id: "cs", name: "Čeština" },
  { id: "en", name: "English" },
  { id: "de", name: "Deutsch" },
  { id: "pl", name: "Polski" },
  { id: "hu", name: "Magyar" },
];

const COLORS = ["#34d399", "#facc15", "#60a5fa", "#f472b6", "#a78bfa", "#fb923c", "#ef4444", "#10b981"];

const PREVIEW_REVIEW = {
  rating: 2,
  text: "Doručenie trvalo 8 dní, balíček prišiel poškodený. Čakal som lepšiu službu za túto cenu.",
  customer_name: "Karol",
  product_name: "Bluetooth slúchadlá",
};

const DEFAULT_STATE = {
  step: 1,
  primary_agent: "review_reply",
  brand_name: "",
  brand_voice: "warm",
  brand_color: "#34d399",
  lang: "sk",
  preview_reply: null,
  email: "",
  workspace: null,
};

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw);
    // If a workspace already exists in storage, jump to success step
    // so a returning user doesn't redo the wizard. They can press
    // "Reset wizardu" on step 5 to start over.
    if (parsed?.workspace?.id) {
      return { ...DEFAULT_STATE, ...parsed, step: 5 };
    }
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

export default function AgentOsOnboardingWizard() {
  const [state, setState] = useState(loadState);
  const update = (patch) => setState((s) => ({ ...s, ...patch }));

  // Persist progress on every state change.
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* quota / private mode */ }
  }, [state]);

  // Capture ?ref=sh_xxx on first mount so the referral survives even if
  // the user navigates away mid-wizard (the activate handler reads it
  // back from localStorage at submit time).
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get("ref");
      if (ref && /^sh_[A-Za-z0-9]{4,40}$/.test(ref)) {
        localStorage.setItem("trp_agent_os_ref", ref);
      }
    } catch { /* SSR / private mode */ }
  }, []);

  const next = () => update({ step: Math.min(5, state.step + 1) });
  const back = () => update({ step: Math.max(1, state.step - 1) });

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="agent-os-onboarding-page">
      <AgentOsPixelTracker event="StartTrial" />

      <header className="border-b border-gray-900 bg-black/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
              <Bot className="w-5 h-5 text-black" />
            </div>
            <span className="font-black tracking-tight">
              TopRankPulse <span className="text-emerald-400">· Onboarding</span>
            </span>
          </a>
          <StepDots current={state.step} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {state.step === 1 && <StepPickAgent state={state} update={update} next={next} />}
        {state.step === 2 && <StepBranding   state={state} update={update} next={next} back={back} />}
        {state.step === 3 && <StepPreview    state={state} update={update} next={next} back={back} />}
        {state.step === 4 && <StepActivate   state={state} update={update} next={next} back={back} />}
        {state.step === 5 && <StepSuccess    state={state} />}
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step indicator                                                     */
/* ------------------------------------------------------------------ */

function StepDots({ current }) {
  return (
    <div className="flex items-center gap-2" data-testid="agent-os-wizard-step-dots">
      {[1, 2, 3, 4, 5].map((s) => (
        <div
          key={s}
          data-testid={`agent-os-wizard-dot-${s}`}
          className={`h-1.5 rounded-sm transition-all ${
            s === current
              ? "w-8 bg-emerald-400"
              : s < current
              ? "w-4 bg-emerald-700"
              : "w-4 bg-gray-800"
          }`}
        />
      ))}
      <span className="ml-2 text-[10px] uppercase tracking-widest text-gray-500 font-mono">
        {current}/5
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Shared navigation                                                  */
/* ------------------------------------------------------------------ */

function WizardNav({ onBack, onNext, nextLabel = "Pokračovať", nextDisabled, loading, testid }) {
  return (
    <div className="mt-8 flex items-center justify-between" data-testid={`agent-os-wizard-nav-${testid}`}>
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          data-testid={`agent-os-wizard-back-${testid}`}
          className="inline-flex items-center gap-1 px-4 py-2 text-gray-400 hover:text-white text-xs font-bold uppercase tracking-widest"
        >
          <ArrowLeft className="w-3 h-3" /> Späť
        </button>
      ) : <span />}

      <button
        type="button"
        onClick={onNext}
        disabled={nextDisabled || loading}
        data-testid={`agent-os-wizard-next-${testid}`}
        className="inline-flex items-center gap-2 px-7 py-3 bg-emerald-400 hover:bg-emerald-300 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black uppercase tracking-widest text-sm transition-colors"
      >
        {loading
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Pracujem…</>
          : <>{nextLabel} <ArrowRight className="w-4 h-4" /></>}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 1 — Pick agent                                                */
/* ------------------------------------------------------------------ */

function StepPickAgent({ state, update, next }) {
  return (
    <section data-testid="agent-os-wizard-step-1">
      <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 font-bold mb-3">Krok 1 z 5</div>
      <h1 className="text-3xl sm:text-4xl font-black mb-3">Vyber prvého agenta</h1>
      <p className="text-base text-gray-400 mb-8">
        Začni s jedným, ďalších pridáš kedykoľvek. <strong className="text-white">Review Reply Agent</strong> je odporúčaný — najrýchlejšie ti ukáže výsledok.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        {AGENTS.map((a) => {
          const selected = state.primary_agent === a.id;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => update({ primary_agent: a.id })}
              disabled={!a.ready}
              data-testid={`agent-os-wizard-agent-${a.id}`}
              className={`relative p-5 text-left border-2 transition-all ${
                selected
                  ? "bg-emerald-950/40 border-emerald-500"
                  : a.ready
                  ? "bg-black border-gray-800 hover:border-emerald-700/40"
                  : "bg-black/60 border-gray-900 opacity-50 cursor-not-allowed"
              }`}
            >
              {!a.ready && (
                <span className="absolute top-2 right-2 px-2 py-0.5 text-[9px] uppercase tracking-widest bg-gray-800 text-gray-400 font-bold">
                  Phase 2
                </span>
              )}
              {selected && (
                <CheckCircle className="absolute top-3 right-3 w-5 h-5 text-emerald-400" />
              )}
              <div className={`mb-3 ${selected ? "text-emerald-400" : "text-gray-500"}`}>{a.icon}</div>
              <h3 className="text-base font-black mb-1">{a.name}</h3>
              <p className="text-xs text-gray-400 mb-3">{a.desc}</p>
              <div className="text-xs font-bold text-emerald-400 tabular-nums">€{a.price}/mes po triale</div>
            </button>
          );
        })}
      </div>
      <WizardNav onNext={next} testid="step1" />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Step 2 — Brand setup                                               */
/* ------------------------------------------------------------------ */

function StepBranding({ state, update, next, back }) {
  const validBrand = state.brand_name.trim().length > 0;
  return (
    <section data-testid="agent-os-wizard-step-2">
      <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 font-bold mb-3">Krok 2 z 5</div>
      <h1 className="text-3xl sm:text-4xl font-black mb-3">Tvoj brand</h1>
      <p className="text-base text-gray-400 mb-8">
        AI agent bude odpovedať vo vašom mene. Daj mu tón, jazyk a farbu.
      </p>

      <div className="space-y-6">
        <div>
          <label className="text-xs text-gray-500 uppercase tracking-widest mb-2 block">Názov e-shopu / brandu</label>
          <input
            type="text"
            data-testid="agent-os-wizard-brand-name"
            value={state.brand_name}
            onChange={(e) => update({ brand_name: e.target.value })}
            placeholder="napr. Krásne Boty SK"
            className="w-full px-4 py-3 bg-black border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600"
          />
        </div>

        <div>
          <label className="text-xs text-gray-500 uppercase tracking-widest mb-2 block">Tón odpovede</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="agent-os-wizard-voices">
            {VOICES.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => update({ brand_voice: v.id })}
                data-testid={`agent-os-wizard-voice-${v.id}`}
                className={`p-3 text-left text-xs border-2 transition-colors ${
                  state.brand_voice === v.id
                    ? "bg-emerald-950/40 border-emerald-500 text-white"
                    : "bg-black border-gray-800 text-gray-400 hover:border-emerald-700/40"
                }`}
              >
                <div className="font-bold">{v.name}</div>
                <div className="text-[10px] opacity-75">{v.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-500 uppercase tracking-widest mb-2 block">Jazyk</label>
          <select
            data-testid="agent-os-wizard-lang"
            value={state.lang}
            onChange={(e) => update({ lang: e.target.value })}
            className="w-full px-4 py-3 bg-black border border-gray-800 focus:border-emerald-500 outline-none text-white"
          >
            {LANGS.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs text-gray-500 uppercase tracking-widest mb-2 block">Brand farba (pre emaily a UI)</label>
          <div className="flex flex-wrap gap-2" data-testid="agent-os-wizard-colors">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => update({ brand_color: c })}
                data-testid={`agent-os-wizard-color-${c.replace("#","")}`}
                aria-label={`Color ${c}`}
                className={`w-10 h-10 rounded-sm transition-transform ${
                  state.brand_color === c ? "ring-2 ring-white ring-offset-2 ring-offset-black scale-110" : "hover:scale-105"
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>
      </div>

      <WizardNav onBack={back} onNext={next} nextDisabled={!validBrand} testid="step2" />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Step 3 — Live preview                                              */
/* ------------------------------------------------------------------ */

function StepPreview({ state, update, next, back }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const generate = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.post(`${API}/agent-os/demo/review-reply`, {
        ...PREVIEW_REVIEW,
        brand_name: state.brand_name || "tvoj shop",
        brand_voice: state.brand_voice,
        lang: state.lang,
      });
      update({ preview_reply: r.data });
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "object" ? d.message : (d || "Skús to ešte raz."));
    }
    setLoading(false);
  };

  // Auto-generate on first entry to the step (only if empty).
  useEffect(() => {
    if (!state.preview_reply && !loading) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accentStyle = useMemo(() => ({ backgroundColor: state.brand_color }), [state.brand_color]);

  return (
    <section data-testid="agent-os-wizard-step-3">
      <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 font-bold mb-3">Krok 3 z 5 · Wow moment</div>
      <h1 className="text-3xl sm:text-4xl font-black mb-3">
        Pozri, ako by <span style={{ color: state.brand_color }}>{state.brand_name || "tvoj brand"}</span> odpovedal
      </h1>
      <p className="text-base text-gray-400 mb-8">
        Skutočná AI generácia s tvojím brandom + tónom. Vzorová 2★ recenzia od Karola.
      </p>

      <div className="p-5 bg-black border border-gray-800 mb-4">
        <div className="text-xs text-gray-500 uppercase tracking-widest mb-2">Recenzia zákazníka</div>
        <div className="flex gap-1 mb-2">
          {[1,2,3,4,5].map((n) => (
            <Star key={n} className={`w-4 h-4 ${n <= PREVIEW_REVIEW.rating ? "fill-rose-400 text-rose-400" : "text-gray-700"}`} />
          ))}
          <span className="ml-2 text-xs text-gray-500">— {PREVIEW_REVIEW.customer_name}, {PREVIEW_REVIEW.product_name}</span>
        </div>
        <p className="text-sm text-gray-300 italic">„{PREVIEW_REVIEW.text}"</p>
      </div>

      <div className="relative p-5 border-2 mb-4" style={{ borderColor: state.brand_color + "60" }}>
        <div className="absolute -top-3 left-4 px-2 text-[10px] uppercase tracking-widest font-bold text-black"
             style={accentStyle}>
          {state.brand_name || "tvoj brand"} · AI Reply
        </div>
        {loading && (
          <div className="text-center py-8 text-emerald-300 text-sm" data-testid="agent-os-wizard-preview-loading">
            <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" />
            AI píše odpoveď s tvojím tónom…
          </div>
        )}
        {err && (
          <div className="text-rose-300 text-sm py-2" data-testid="agent-os-wizard-preview-error">{err}</div>
        )}
        {state.preview_reply && !loading && (
          <div data-testid="agent-os-wizard-preview-result">
            <p className="text-white text-base leading-relaxed mb-3">{state.preview_reply.reply}</p>
            <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-widest">
              <span className="px-2 py-1 font-bold text-black" style={accentStyle}>{state.preview_reply.intent}</span>
              <span className="px-2 py-1 bg-gray-900 text-gray-400">{state.preview_reply.latency_ms} ms</span>
              {state.preview_reply.used_fallback && (
                <span className="px-2 py-1 bg-amber-900/30 text-amber-300 font-bold">Fallback</span>
              )}
            </div>
          </div>
        )}
        {!loading && state.preview_reply && (
          <button
            type="button"
            onClick={generate}
            data-testid="agent-os-wizard-preview-regen"
            className="mt-4 inline-flex items-center gap-1 text-xs text-gray-400 hover:text-emerald-300"
          >
            <Sparkles className="w-3 h-3" /> Vygeneruj inú verziu
          </button>
        )}
      </div>

      <WizardNav
        onBack={back}
        onNext={next}
        nextLabel="Páči sa mi to → ďalej"
        nextDisabled={!state.preview_reply}
        testid="step3"
      />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Step 4 — Email + activate                                          */
/* ------------------------------------------------------------------ */

function StepActivate({ state, update, next, back }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email);

  const activate = async () => {
    if (!validEmail) return;
    setLoading(true); setErr(null);
    try {
      // Referral attribution: ?ref=sh_xxx forwarded to backend so the
      // referrer share gets credited. Persisted in localStorage so a
      // user who lands via ref but clicks around before activating
      // still credits the referrer.
      let ref = null;
      try {
        const params = new URLSearchParams(window.location.search);
        ref = params.get("ref");
        if (ref) {
          localStorage.setItem("trp_agent_os_ref", ref);
        } else {
          ref = localStorage.getItem("trp_agent_os_ref");
        }
      } catch { /* SSR / private mode */ }

      const r = await axios.post(`${API}/agent-os/onboarding/activate`, {
        email: state.email.trim().toLowerCase(),
        primary_agent: state.primary_agent,
        brand_name: state.brand_name.trim(),
        brand_voice: state.brand_voice,
        brand_color: state.brand_color,
        lang: state.lang,
        source: ref ? "share_referral" : "onboarding_wizard",
        ref: ref || undefined,
      });
      update({ workspace: r.data.workspace });
      if (typeof window !== "undefined" && window.fbq) {
        window.fbq("track", "StartTrial", { value: 99, currency: "EUR" });
      }
      // V141.4 — log conversion against the assigned A/B variant so we
      // can compute per-variant conversion rate in the admin stats.
      try {
        const variant = localStorage.getItem("ab_funnel_variant_v1");
        if (variant) {
          axios.post(`${API}/concierge/track`, {
            event: "conversion", trigger: variant,
            path: "/agent-os/onboard",
          }).catch(() => {});
        }
      } catch { /* localStorage blocked */ }
      // Clear referral cookie once credited so subsequent activations
      // from the same browser don't double-credit.
      try { localStorage.removeItem("trp_agent_os_ref"); } catch {}
      next();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Skús to ešte raz.");
    }
    setLoading(false);
  };

  return (
    <section data-testid="agent-os-wizard-step-4">
      <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 font-bold mb-3">Krok 4 z 5 · Posledný krok</div>
      <h1 className="text-3xl sm:text-4xl font-black mb-3">Aktivuj 14-dňový trial</h1>
      <p className="text-base text-gray-400 mb-8">
        Iba email — žiadna karta, žiadny záväzok. Dnes začneš, o 14 dní rozhodneš.
      </p>

      <div className="p-5 bg-gradient-to-br from-emerald-950/30 to-black border-2 border-emerald-700/40 mb-6">
        <div className="text-xs text-gray-500 uppercase tracking-widest mb-3">Tvoj setup</div>
        <ul className="space-y-2 text-sm">
          <li><span className="text-gray-500">Agent:</span> <strong className="text-white">{AGENTS.find((a) => a.id === state.primary_agent)?.name}</strong></li>
          <li><span className="text-gray-500">Brand:</span> <strong className="text-white">{state.brand_name}</strong></li>
          <li><span className="text-gray-500">Tón:</span> <strong className="text-white">{VOICES.find((v) => v.id === state.brand_voice)?.name}</strong></li>
          <li><span className="text-gray-500">Jazyk:</span> <strong className="text-white">{LANGS.find((l) => l.id === state.lang)?.name}</strong></li>
        </ul>
      </div>

      <input
        type="email"
        required
        data-testid="agent-os-wizard-email"
        placeholder="Tvoj email"
        value={state.email}
        onChange={(e) => update({ email: e.target.value })}
        className="w-full px-4 py-3 bg-black border border-gray-800 focus:border-emerald-500 outline-none text-white placeholder-gray-600 mb-4"
      />

      {err && (
        <div className="p-3 bg-rose-950/30 border border-rose-700/40 text-rose-200 text-sm mb-4" data-testid="agent-os-wizard-error">
          {err}
        </div>
      )}

      <p className="text-xs text-gray-600 mb-4 flex items-center gap-2">
        <ShieldCheck className="w-3 h-3" />
        Bez karty · GDPR · 1-click cancel
      </p>

      <WizardNav onBack={back} onNext={activate} nextDisabled={!validEmail} loading={loading} nextLabel="Aktivovať trial" testid="step4" />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Step 5 — Success                                                   */
/* ------------------------------------------------------------------ */

function StepSuccess({ state }) {
  const ws = state.workspace || {};
  const accentStyle = { backgroundColor: state.brand_color };
  const trialEnd = (ws.trial_end || "").slice(0, 10);

  return (
    <section data-testid="agent-os-wizard-step-5">
      <div className="text-center mb-10">
        <div className="inline-flex w-20 h-20 rounded-full items-center justify-center mb-6" style={accentStyle}>
          <CheckCircle className="w-12 h-12 text-black" />
        </div>
        <h1 className="text-3xl sm:text-5xl font-black mb-3">
          Hotovo! <span style={{ color: state.brand_color }}>{state.brand_name}</span> je live. 🚀
        </h1>
        <p className="text-base text-gray-400 max-w-xl mx-auto">
          Tvoj <strong className="text-white">{AGENTS.find((a) => a.id === state.primary_agent)?.name}</strong> je aktivovaný.
          Trial beží do <strong className="text-white">{trialEnd}</strong>.
          Welcome email + onboarding link sme ti poslali na <strong className="text-white">{state.email}</strong>.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        <NextStepCard
          n={1}
          title="Otvor dashboard"
          desc="Prepoj svoj e-shop (Heureka / Woo / Shopify)"
          color={state.brand_color}
          href={`/agent-os/workspace/${ws.id || ""}`}
          testid="success-dashboard"
        />
        <NextStepCard
          n={2}
          title="Schváľ prvé 3 odpovede"
          desc="Agent sa učí z tvojho feedbacku"
          color={state.brand_color}
          href={`/agency`}
          testid="success-approve"
        />
        <NextStepCard
          n={3}
          title="Pridaj 2. agenta"
          desc="Bundle 3 za 2 — tretí zadarmo"
          color={state.brand_color}
          href="/agent-os/eshop#agent-picker"
          testid="success-bundle"
        />
      </div>

      <div className="text-center">
        <a
          href={`/agent-os/workspace/${ws.id || ""}`}
          data-testid="agent-os-wizard-success-cta"
          className="inline-flex items-center gap-2 px-7 py-3 text-black font-black uppercase tracking-widest text-sm"
          style={accentStyle}
        >
          Otvoriť dashboard <ArrowRight className="w-4 h-4" />
        </a>
        <button
          type="button"
          onClick={() => { localStorage.removeItem(STORAGE_KEY); window.location.href = "/"; }}
          className="block mx-auto mt-4 text-xs text-gray-500 hover:text-gray-300"
        >
          Reset wizardu
        </button>
      </div>
    </section>
  );
}

function NextStepCard({ n, title, desc, color, href, testid }) {
  return (
    <a
      href={href}
      data-testid={`agent-os-wizard-${testid}`}
      className="block p-5 bg-black border border-gray-800 hover:border-emerald-700/40 transition-colors"
    >
      <div className="text-2xl font-black tabular-nums mb-2" style={{ color }}>#{n}</div>
      <h3 className="text-sm font-black mb-1">{title}</h3>
      <p className="text-xs text-gray-400">{desc}</p>
    </a>
  );
}
